import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { workspaceSlug } from "../lib/hosts";
import { useProject, useProjectByKey } from "../lib/projects";
import { useWorkspace } from "../lib/workspaces";
import { idSchema } from "../../shared/schemas/issues";
import { ProjectHeading } from "../components/projects/ProjectHeading";
import { SprintReports, VelocityReport } from "../components/reports/SprintReports";
import { FlowReports } from "../components/reports/FlowReports";
import { ProjectKeyMoved, ProjectUnreachable } from "../components/projects/ProjectStates";
import { Skeleton } from "../components/ui/skeleton";
import { Unavailable } from "../components/ui/states";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { ApiError } from "../lib/api";
import { cn } from "../lib/cn";
import { strings } from "../lib/strings";

const words = strings.reports;
const KINDS = ["sprint", "velocity", "flow"] as const;
type ReportKind = (typeof KINDS)[number];

/** The reports' address: which report, which sprint, and the flow reports' days, grouping and what they show. */
const reportsSearchSchema = z.object({
  report: z.enum(KINDS).optional().catch(undefined),
  sprint: idSchema.optional().catch(undefined),
  days: z.coerce
    .number()
    .refine((days) => days === 30 || days === 90)
    .optional()
    .catch(undefined),
  interval: z.enum(["day", "week"]).optional().catch(undefined),
  by: z.enum(["category", "status"]).optional().catch(undefined),
});

/** A project's reports (BUILD_SPEC §16): what happened in it, from its record, each with how it is counted. */
function ReportsPage() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/reports" });
  const search = useSearch({ from: "/admin/p/$projectKey/reports" });
  const navigate = useNavigate({ from: "/admin/p/$projectKey/reports" });
  const slug = workspaceSlug();
  const view = useWorkspace(slug);
  const byKey = useProjectByKey(slug, projectKey);
  const project = byKey.project;
  const detail = useProject(project?.id ?? "");
  if (view.isLoading || byKey.isLoading)
    return (
      <div className="mx-auto max-w-6xl space-y-2 px-6 py-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  if ((view.error instanceof ApiError && (view.error.status === 404 || view.error.status === 401)) || !view.data)
    return <ToWorkspaces />;
  if (!byKey.loaded)
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Unavailable what={strings.projects.list.theProjects} onRetry={byKey.refetch} page />
      </div>
    );
  if (!project || project.joinable) return <ProjectUnreachable project={project} />;
  if (project.key !== projectKey) return <ProjectKeyMoved from={projectKey} to={project.key} />;
  // Sprint reports and velocity are a Scrum project's; every project has its flow.
  const kinds: readonly ReportKind[] = project.method === "scrum" ? KINDS : ["flow"];
  const kind: ReportKind = search.report !== undefined && kinds.includes(search.report) ? search.report : kinds[0]!;
  const current = detail.data?.project ?? project;
  return (
    <div className="mx-auto max-w-6xl space-y-6 px-6 py-8">
      <header className="flex flex-col gap-4">
        <ProjectHeading
          project={current}
          projectKey={projectKey}
          current="reports"
          canManage={detail.data?.canManage ?? false}
        />
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.body}</p>
        {kinds.length > 1 && (
          <div
            role="group"
            aria-label={words.kindsLabel}
            className="inline-flex w-fit rounded-md border border-[hsl(var(--border))] p-0.5"
          >
            {kinds.map((each) => (
              <button
                key={each}
                type="button"
                aria-pressed={kind === each}
                onClick={() =>
                  void navigate({ search: (prev) => ({ ...prev, report: each === kinds[0] ? undefined : each }) })
                }
                className={cn(
                  "rounded px-2.5 py-1 text-sm focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:outline-none",
                  kind === each
                    ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                    : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))]",
                )}
              >
                {words.kinds[each]}
              </button>
            ))}
          </div>
        )}
      </header>
      {kind === "sprint" && (
        <SprintReports
          project={current}
          sprintId={search.sprint}
          onSprint={(sprint) => void navigate({ search: (prev) => ({ ...prev, sprint }) })}
        />
      )}
      {kind === "velocity" && <VelocityReport project={current} />}
      {kind === "flow" && (
        <FlowReports
          project={current}
          days={search.days === 90 ? 90 : 30}
          interval={search.interval ?? "day"}
          by={search.by ?? "category"}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/reports")({
  validateSearch: reportsSearchSchema,
  component: ReportsPage,
});
